
import type { PropType } from 'vue'
import type { ToolReportEntry } from '../../../models/domain/tool-report-entry'
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from 'vue'
import KpiBar from '../../../components/kpi-bar.component.vue'
import { EpbBadge } from '../../../components/shared/epb-badge'
import { EpbButton } from '../../../components/shared/epb-button'
import { EpbLoading } from '../../../components/shared/epb-loading'
import { EpbPill } from '../../../components/shared/epb-pill'
import { EpbText } from '../../../components/shared/epb-text'
import { useDateFormat } from '../../../composables/use-date-format'
import { useScheduleFormat } from '../../../composables/use-schedule-format'
import { useToolingReportToolConfiguration } from '../../../composables/use-tooling-report-tool-configuration'
import { FeatureIdEnum, KpiEnum } from '../../../enums'
import { ROUTER_CONSTS } from '../../../router/router.consts'
import EpbAside from '../../aside.component.vue'
import { UiAlert } from '../../keen-ui'
import PromotionCard from '../../promotions/promotion-card.component.vue'
import ToolReportingAsideSection from './aside-section.component.vue'
import ToolReportingAsideConfigurationState from './aside/configuration-state.component.vue'
import DailyChart from './daily-chart.component.vue'

export default defineComponent({
  name: 'ToolReportingAside',
  components: {
    EpbLoading,
    DailyChart,
    EpbAside,
    PromotionCard,
    EpbBadge,
    EpbButton,
    EpbPill,
    EpbText,
    KpiBar,
    UiAlert,
    ToolReportingAsideSection,
    ToolReportingAsideConfigurationState,
  },
  props: {
    tool: {
      type: String as PropType<FeatureIdEnum>,
      required: true,
    },
    toolReportEntry: {
      type: Object as PropType<ToolReportEntry>,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $router, $epb },
    } = getCurrentInstance()

    const baseClass = 'epb-tool-reporting-aside'

    const messages = $epb.messages.insights.toolReporting.aside

    const toolConfiguration = ref()
    const loading = ref(false)

    const name = computed(() => toolConfiguration.value?.title ?? props.toolReportEntry.name)
    const position = computed(() =>
      formatPosition(toolConfiguration.value?.position ?? props.toolReportEntry.averagePosition),
    )
    const positionTitle = computed(() =>
      toolConfiguration.value?.position
        ? messages.sections.position
        : messages.sections.averagePosition.title,
    )

    const ctr = computed(() => ({
      id: KpiEnum.CLICKS,
      label: KpiEnum.CLICKS,
      percentage: props.toolReportEntry.ctr ?? 0,
    }))

    function formatPosition(position: number): string {
      if (Number.isInteger(position)) {
        return position.toFixed(0)
      }

      return position.toFixed(1)
    }

    const formattedCreationDateInfo = computed(() => {
      return formatInfo(
        messages.sections.created.creationDate,
        useDateFormat(toolConfiguration.value.meta.creationDate),
      )
    })

    const formattedUserInfo = computed(() => {
      return formatInfo(messages.sections.created.user, toolConfiguration.value.meta.user)
    })

    function formatInfo(label: string, value: string): string {
      return [label, value].join(': ')
    }

    function onEditButtonClick(): void {
      $router.push({
        name: ROUTER_CONSTS.routes[FeatureIdEnum.PROMOTION].update,
        params: {
          [ROUTER_CONSTS.params.id]: toolConfiguration.value.id,
        },
      })
    }

    onMounted(async () => {
      loading.value = true
      toolConfiguration.value = await useToolingReportToolConfiguration({
        tool: props.tool,
        id: props.toolReportEntry.id,
      })
      loading.value = false
    })

    return {
      baseClass,
      ctr,
      KpiEnum,
      messages,
      loading,
      name,
      position,
      positionTitle,
      onEditButtonClick,
      useScheduleFormat,
      toolConfiguration,
      formattedCreationDateInfo,
      formattedUserInfo,
    }
  },
})
